import { CIP, FUNDING_SOURCE_ERROR_ALERTS } from '../../constants'
import { get, isEqual, uniqWith, sortBy } from 'lodash'
import { isOpenBankingTreatment } from '../../../experiments/openBanking'
import { INSTRUMENT_TYPES } from '../../../../../lib/constants'
import * as sharedUtils from '../../../../../lib/utils/fundingOptionSharedUtils'
import { hasFIFailuresEnhancementTrmnt } from '../../../experiments/fiFailuresEnhancement'
export * from '../../../../../lib/utils/fundingOptionSharedUtils'

/**
 * Adds dash to source id. This is needed for Open Banking when sending the FI
 * ID to wallet.
 * @param {String} id funding source id
 * @returns {String}
 */
export function formatSourceId(id) {
  const isFormatted = sharedUtils.specialCharactersRegex.test(id)
  if (isFormatted) {
    return id
  }
  return `${id.slice(0, 2)}-${id.slice(2)}`
}

/**
 * decode html entities, e.g. &#174 -> ®
 * code https://stackoverflow.com/questions/5796718/html-entity-decode
 */
export const decodeEntities = (function() {
  // this prevents any overhead from creating the object each time
  const element = document.createElement('div')

  function decodeHTMLEntities(str) {
    if (str && typeof str === 'string') {
      // strip script/html tags
      str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gim, '')
      str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gim, '')
      element.innerHTML = str
      str = element.textContent
      element.textContent = ''
    }

    return str
  }

  return decodeHTMLEntities
})()

/**
 *
 * A helper to search through funding options and pluck out a source which is preferred.
 * It's possible for this source to appear in multiple options, so we just grab it the first time we see it.
 * @return {object|undefined}
 */
export function getPreferredFundingSource(fundingOptions) {
  for (let i = 0; i < fundingOptions.length; i++) {
    const { funding_sources } = fundingOptions[i]
    const preferredFundingSource = funding_sources.find(source => {
      return source.isPreferred
    })
    if (preferredFundingSource) {
      // immediately return from the function as soon as we've found it in any option
      return preferredFundingSource
    }
  }
  return undefined
}

/**
 * Grab the funding option which includes ONLY the preferred funding source if available.
 * @return {object|undefined}
 */
export function getOptionByPreferredSource(fundingOptions) {
  const preferredFundingSource = getPreferredFundingSource(fundingOptions)
  if (!preferredFundingSource) {
    return
  }
  return fundingOptions.find(({ funding_sources }) => {
    return funding_sources.every(
      source => source.id === preferredFundingSource.id
    )
  })
}

/**
 * Grab the funding option which includes ONLY the argument card source, if available.
 * @return {object|undefined}
 */
export function getOptionByCard(fundingOptions, card) {
  if (!card) {
    return
  }
  return fundingOptions.find(({ funding_sources }) => {
    return funding_sources.every(source => {
      return source.fi_last_4 && source.fi_last_4 === card.lastDigits
    })
  })
}

/**
 * Tool to keep only the options in a funding options list that make sense given the partial balance toggles that are
 * selected. e.g. "get only options including split EUR bal if toggled (and be sure to exclude any balance only options!)".
 *
 * There is one REALLY IMPORTANT CAVEAT: because of product requirement, PayPal Balance and other options which contain
 * a single source that cannot be split (e.g. PayPal Credit) should always be relevant even if they don't match toggled balances
 */
export function getRelevantOptionsByPartialBalances(
  fundingOptions,
  toggledBalanceSourceIds
) {
  // if there are no toggled balances, only non-split card/bank or balance options are relevant
  if (!toggledBalanceSourceIds.length) {
    return fundingOptions.filter(option => {
      return (
        option.funding_sources.length === 1 || isPayPalBalanceOnlyOption(option)
      )
    })
  }

  return fundingOptions.filter(option => {
    // IMPORTANT: allow any options which CANNOT be split (e.g. PayPal Credit, PayPal Balance) to remain in the list
    if (!isOptionWithPair(option, fundingOptions)) {
      return true
    }

    // otherwise keep any option which has the same partial balance as what's toggled
    const partialBalanceSourceIds = getPartialBalanceSourcesFromOption(
      option
    ).map(source => source.id)
    return isEqual(
      sortBy(partialBalanceSourceIds),
      sortBy(toggledBalanceSourceIds)
    )
  })
}

/**
 * This util will tell you if the argument funding option contains a pair with under the new partial balance paradigm.
 * (e.g. if Visa only option and Visa + USD Bal option exists in fundingOptions, both options would return true, because
 * they have a pair. PayPal Credit is never split with balance, so it would return false.)
 * @return {bool}
 */
export function isOptionWithPair(targetOption = {}, fundingOptions) {
  // balance only options don't have a pair
  if (isPayPalBalanceOnlyOption(targetOption)) {
    return false
  }

  const { id: targetSourceId } = getPrimarySourceFromOption(targetOption) || {}
  const optionsWhichIncludeTargetSource = fundingOptions.filter(
    ({ funding_sources }) => {
      return funding_sources.some(source => source.id === targetSourceId)
    }
  )

  const isSplitOption =
    targetOption.funding_sources && targetOption.funding_sources.length > 1
  // TODO make this condition cleaner, and more self explanatory (the right half is for when all funding options are split - method initially thought it's not splitable)
  return !!(optionsWhichIncludeTargetSource.length > 1 || isSplitOption)
}

/**
 * Get single funding option except unclaimed balance
 * @param fundingOptions
 * @returns {boolean|*}
 */
export function getSingleFundingOption(fundingOptions) {
  const singleFundingOption =
    fundingOptions[0].id !== CIP.CIP_REQUIRED && fundingOptions[0]
  return singleFundingOption
}

/**
 * Helper for when we receive funding options to automatically select funding options where applicable. 1. try option
 * from card just added 2. try sources that were previously selected (maybe amount changed) 3. grab preferred...
 * OTHERWISE return undefined
 * @return {object|undefined}
 */
export function getAutoSelectedFundingOption(
  fundingOptions,
  isPurchase,
  sourceIdsToMatch,
  card,
  ignorePreferred
) {
  // if there is only one funding options, we should always auto select it
  // But if the single funding option is nobalance/ unclaimed balance, do not autoselect it
  if (fundingOptions.length === 1) {
    return getSingleFundingOption(fundingOptions)
  }

  const matchFromPreviousOption = sharedUtils.getOptionBySourceIds(
    fundingOptions,
    sourceIdsToMatch
  )
  const optionFromCard = getOptionByCard(fundingOptions, card)
  // we never honor preferred funding sources in friends and family (for now)
  // neither when a funding option just been added via Open Banking
  const optionFromPreferredSource =
    isPurchase && !ignorePreferred
      ? getOptionByPreferredSource(fundingOptions)
      : undefined
  const partialMatchFromPreviousOption = sharedUtils.getOptionBySourceIdsPartialMatch(
    fundingOptions,
    sourceIdsToMatch
  )
  const autoSelectedFundingOption =
    optionFromCard ||
    matchFromPreviousOption ||
    optionFromPreferredSource ||
    partialMatchFromPreviousOption
  return autoSelectedFundingOption // autoSelectedFundingOption could technically be 'false'
}

export function justSelectedFundingOption(previousProps, currentProps) {
  const {
    selectedFundingOption: { id: previousId = '' },
  } = previousProps
  const {
    selectedFundingOption: { id: currentId = '' },
  } = currentProps
  const selectedOptionWasCleared = previousId && !currentId
  return !selectedOptionWasCleared && previousId !== currentId
}

/**
 * Returns true if selected source is updated to/from preferred
 * @return {bool}
 */
export function justSetPreferredSource(previousProps, currentProps) {
  const {
    selectedFundingOption: { id: previousId = '' },
  } = previousProps
  const {
    selectedFundingOption: { id: currentId = '' },
  } = currentProps

  const previousPreferredFundingSource =
    getPreferredFundingSource(previousProps.fundingOptions) || {}
  const currentPreferredFundingSource =
    getPreferredFundingSource(currentProps.fundingOptions) || {}

  if (previousId && currentId && previousId === currentId) {
    return (
      previousPreferredFundingSource.id !== currentPreferredFundingSource.id
    )
  }
}

export function justFetchedFundingOptions(previousProps, currentProps) {
  const {
    hasFetchedFundingOptions: previousHasFetchedFundingOptions,
  } = previousProps
  const {
    hasFetchedFundingOptions: currentHasFetchedFundingOptions,
  } = currentProps
  return (
    previousHasFetchedFundingOptions === false &&
    currentHasFetchedFundingOptions === true
  )
}

/**
 * Will return true if the funding option consists of sources which are all balance (can be across any number of currencies)
 * @return {bool}
 */
export function isPayPalBalanceOnlyOption(fundingOption) {
  const { funding_sources = [] } = fundingOption
  if (!fundingOption.id) {
    return false
  }
  return funding_sources.every(
    source => source.instrument_type === INSTRUMENT_TYPES.HOLDING
  )
}

/**
 * Will return if the funding option is a disallowed source or a balance that needs CIP
 * @param fundingOption
 * @returns {boolean}
 */
export function isUnclaimedBalanceCIPRequired(fundingOption) {
  return fundingOption.id === CIP.CIP_REQUIRED
}

export function shouldShowFISelector(prevProps, currentProps) {
  const {
    fundingOptions: currentFundingOptions = [],
    disallowedSources: currentDisallowedSources = [],
    selectedFundingOption: currentSelectedFundingOption,
    successfullyAddedFundingInstrumentIds,
    unavailableFI,
  } = currentProps
  const hasSelectedFundingOption = !!currentSelectedFundingOption.id
  const showCashAdvanceInterstitial = shouldForceShowCashAdvanceInterstitial(
    currentProps
  )
  const newFundingOptionIsAvailable = sharedUtils.isFundingOptionAvailable(
    currentFundingOptions,
    successfullyAddedFundingInstrumentIds
  )
  const selectedFOHasRTBContingency =
    isOpenBankingTreatment() && currentSelectedFundingOption.hasRTBContingency

  // if the selected FO has a rtb contingency,  we need the user to select the FI to use
  if (selectedFOHasRTBContingency) {
    return true
  }

  // Coming from Open Banking confirmation: move the flow to the review page
  if (successfullyAddedFundingInstrumentIds && newFundingOptionIsAvailable) {
    // if successfullyAddedFundingInstrumentIds is a csv of FI Ids, we need the user to select the FI to use
    return successfullyAddedFundingInstrumentIds.includes(',')
  }

  // NOTE: In mocks mode justFetchedFUndingOptions returns false, thus
  //       dropping into this if statement and return false and not update
  //       and show the the cash advance interstitial, but on local dev with
  //       a real service call it correctly does returns true.
  if (!justFetchedFundingOptions(prevProps, currentProps)) {
    return false
  }

  /**
   * @IMPORTANT
   * =======================================================================
   * Everything after this comment means funding options were just fetched!!
   * =======================================================================
   *
   * Fetching funding options can happen either by planning (first time) or
   * replanning (planning call with same txn token).
   */

  if (hasFIFailuresEnhancementTrmnt()) {
    const isSelectedInstrumentDeclined =
      hasSelectedFundingOption &&
      hasInstrumentDeclinedSource(currentSelectedFundingOption.funding_sources)

    const isAnyDisallowedInstrumentDeclined = hasInstrumentDeclinedSource(
      currentDisallowedSources
    )
    const hasDisallowedSourcesOnly =
      currentFundingOptions.length === 0 && currentDisallowedSources.length > 0

    // If the user has only disallowed sources after plan/replan, we show the
    // Disallowed FI Page (FI Page -> <DisallowedFundingSources/> component).
    if (hasDisallowedSourcesOnly) {
      return true
    }

    // If the user has either a selected FO or disallowed FI with
    // INSTRUMENT_DECLINED, we show the error alert on top of the FI Page
    if (isSelectedInstrumentDeclined || isAnyDisallowedInstrumentDeclined) {
      return true
    }
  }

  // the user fetched, but probably needs to add card, because the service returned no fulfillable options
  const fulfillableOptionsExist = !!currentFundingOptions.length
  if (!fulfillableOptionsExist) {
    return false
  }

  // Force Show IF one of these conditions applies:
  // - Cash Advance Treatment AND Fee Applies
  // - The FI added in wallet during sendmoney can't be used in this transaction
  if (
    showCashAdvanceInterstitial ||
    (unavailableFI && currentFundingOptions.length > 0)
  ) {
    return true
  }

  // show if nothing has been auto selected at this point, the user needs to be able to see selector to make selection
  return !hasSelectedFundingOption
}

// Determines whether we should force show the cash advance interstitial
export function shouldForceShowCashAdvanceInterstitial(currentProps) {
  const { selectedFundingOption: currentSelectedFundingOption } = currentProps
  const hasSelectedFundingOption = !!currentSelectedFundingOption.id
  if (hasSelectedFundingOption) {
    const { funding_sources = [] } = currentSelectedFundingOption
    const hasCashAdvanceFees = funding_sources.some(
      source => source.cashAdvanceFeeApplies === true
    )
    if (hasCashAdvanceFees) {
      return true
    }
  }
}

/**
 * A temporary helper that will determine (within the action creator of receivedFundingOptions)
 * whether we should force the selection of some funding option (because the interstitial will not be shown)
 * TODO: if interstitial testing doesn't stop, decouple auto selecting an option with whether the interstitial should show...
 */
// TODO test this
export function shouldForceAutoSelectedOption(
  isPurchase,
  shouldShowSelectFundingOptionsPage
) {
  const isFriendsAndFamily = !isPurchase

  // is F&F, and is in shouldShowSelectFundingOptionsPage config... but not in other experiment to show interstitial so we need to
  // make sure shouldShowFISelector sees something is already selected by forcing the selection of the first option...
  return isFriendsAndFamily && !shouldShowSelectFundingOptionsPage
}

/**
 * Obtain all error alerts from the funding sources of a funding option.
 * @param {FundingOption} fundingOption
 * @returns {boolean}
 */
export function getErrorAlerts(fundingOption) {
  const errors = []
  const fundingSources = get(fundingOption, 'funding_sources', [])
  for (const error of FUNDING_SOURCE_ERROR_ALERTS) {
    const hasError = fundingSources.some(source => source[error] === true)
    if (hasError) {
      errors.push(error)
    }
  }
  return errors
}

/**
 * Determine if a funding option contains an instrument with an error alert.
 * @param {FundingOption} fundingOption
 * @returns {boolean}
 */
export function hasErrorAlert(fundingOption) {
  return getErrorAlerts(fundingOption).length > 0
}

export function getPrimarySourceFromOption({ funding_sources = [] }) {
  return funding_sources.find(
    source => source.instrument_type !== INSTRUMENT_TYPES.HOLDING
  )
}

/**
 * Return ALL partial balance sources from SINGLE funding option
 * @return {array}
 */
export function getPartialBalanceSourcesFromOption(fundingOption) {
  if (isPayPalBalanceOnlyOption(fundingOption)) {
    return []
  }

  // there must be partial balance source, find it
  const { funding_sources = [] } = fundingOption
  return funding_sources.filter(source => {
    return source.instrument_type === INSTRUMENT_TYPES.HOLDING
  })
}

/**
 * Return ALL partial balance sources from ALL funding options
 * @return {array}
 */
export function getAllPartialBalanceSourcesFromOptions(fundingOptions = []) {
  const listWithDuplicates = fundingOptions.reduce((newList, option) => {
    return newList.concat(getPartialBalanceSourcesFromOption(option))
  }, [])

  return uniqWith(listWithDuplicates, isEqual)
}

/**
 * Util that can be used for hot swapping selecting funding options when balance toggle is changed. This will accept a
 * target option and a list of funding options, and will return the funding option from the list with the same primary
 * source id. (e.g. Visa option would have pair Visa + USD Bal)
 * @param targetOption {Object} - the funding option for which you want to its split counterpart
 * @param fundingOptions {Object} - the funding options list you want to find a match within
 * @return {Object} - returns a funding option object or empty object if no match
 */
export function getCounterpartFundingOption(targetOption, fundingOptions) {
  if (!targetOption.id) {
    return {}
  }
  const { id: sourceIdToMatch } = getPrimarySourceFromOption(targetOption) || {}
  const matchingFundingOption = fundingOptions.find(option => {
    return option.funding_sources.some(source => source.id === sourceIdToMatch)
  })
  return matchingFundingOption || {}
}

/**
 * Used to grab the actual source from an option (of many sources) we want to show in the UI.
 */
export function getRelevantFundingSource(fundingOption) {
  if (isPayPalBalanceOnlyOption(fundingOption)) {
    const { funding_sources = [] } = fundingOption
    return funding_sources[0] // even if two balance sources available here, for this component it doesn't matter which is grabbed
  }

  return getPrimarySourceFromOption(fundingOption)
}

/**
 * Return a copy of the SOURCE object with isPreferred set to false
 */
// TODO test the below methods
export function removePreferredFromSource(source) {
  return { ...source, isPreferred: false }
}

/**
 * Return a copy of the SOURCE object with isPreferred set to true
 */
export function addPreferredToSource(source) {
  return { ...source, isPreferred: true }
}

/**
 * Return a copy of an OPTION object where isPreferred is set to false for all of it's sources
 */
export function removePreferredFromSourcesInOption(option) {
  const sourcesWithPreferredRemoved = option.funding_sources.map(
    removePreferredFromSource
  )
  return { ...option, funding_sources: sourcesWithPreferredRemoved }
}

/**
 * Return a copy of an OPTION object where isPreferred is set to true on the targetSourceId
 */
export function addPreferredToSourceInOption(option, targetSourceId) {
  const sourcesWithPreferredAdded = option.funding_sources.map(source => {
    const matchBalanceSourceIds = /^HO/
    const sourceId = matchBalanceSourceIds.test(source.id)
      ? 'BALANCE'
      : source.id
    // if source doesn't match target source, don't change it
    if (sourceId !== targetSourceId) {
      return { ...source }
    }
    return { ...source, isPreferred: true }
  })
  return { ...option, funding_sources: sourcesWithPreferredAdded }
}

export function isSendAbroadFlow() {
  if (
    window &&
    window.location &&
    window.location.pathname &&
    window.location.pathname.includes('fx/fees')
  ) {
    return true
  }
  return false
}

export function findInstrumentDeclinedSource(sources) {
  return sources.find(source => source.instrumentDeclined)
}

export function hasInstrumentDeclinedSource(sources) {
  return !!findInstrumentDeclinedSource(sources)
}
